import { Tooltip, TooltipTrigger, TooltipContent, Text, Icon, Badge, cn } from '@flatfile/baseline'
import * as React from 'react'
import { ReactNode, SyntheticEvent, createContext } from 'react'
import { Product } from '../../../api/types'
import {
  IPlan,
  displayPriceFormatted,
  findPlanById,
  getIntervalAdverb,
  getPlanPrice,
  planNameDisplay,
} from '../utilities/billingHelpers'
import {
  getPaidTierUnitAmount,
  productIdIsPro,
  prepayProductIsAvailable,
  prepayProductIsAvailableSelfServe,
} from '../utilities/stripe'

type SelectedProductsContextType = {
  allProducts: Product[]
  selectedPlan: string | undefined
  setSelectedPlan: (id: string | undefined) => void
  selectedFileCommitment: string | undefined
  setSelectedFileCommitment: (id: string | undefined) => void
  selectedPDVCommitment: string | undefined
  setSelectedPDVCommitment: (id: string | undefined) => void
}

const SelectedProductsContext = createContext<SelectedProductsContextType>({
  allProducts: [],
  selectedPlan: '',
  setSelectedPlan: () => {},
  selectedFileCommitment: '',
  setSelectedFileCommitment: () => {},
  selectedPDVCommitment: '',
  setSelectedPDVCommitment: () => {},
})

const BillingWrapper = ({ children }: { children: ReactNode }) => (
  <div className='flex flex-col p-6 gap-4' style={{ overflow: 'visible', height: 'auto', minHeight: '100%' }}>
    {children}
  </div>
)

const BillingContent = ({ children }: { children: ReactNode }) => (
  <div className='flex flex-col gap-8' style={{ overflow: 'visible' }}>
    {children}
  </div>
)

const BillingSection = ({ children }: { children: ReactNode }) => <div className='flex flex-col gap-4'>{children}</div>

const SectionHeader = ({ children }: { children: ReactNode }) => {
  return (
    <Text variant='title2' emphasized>
      {children}
    </Text>
  )
}

const SectionSubHeader = ({ children }: { children: ReactNode }) => {
  return (
    <Text variant='title3' emphasized>
      {children}
    </Text>
  )
}

const CurrentBadge = ({
  disabled,
  disabledTooltipContent,
}: {
  disabled?: boolean
  disabledTooltipContent?: string
}) => (
  <>
    {disabled ? (
      <Tooltip>
        <TooltipTrigger>
          <Badge variant='secondary' className='bg-gray-3 text-gray-12'>
            Current plan
          </Badge>
        </TooltipTrigger>
        <TooltipContent className='flex items-center px-2 py-1'>
          <Text variant='body' className='text-sm'>
            {disabledTooltipContent}
          </Text>
        </TooltipContent>
      </Tooltip>
    ) : (
      <Badge variant='secondary' className='bg-gray-3 text-gray-12'>
        Current plan
      </Badge>
    )}
  </>
)

const PlanTitleWrapper = ({ children }: { children: ReactNode }) => (
  <div className='flex justify-between mb-[-12px] items-center'>{children}</div>
)

const CommitmentItem = ({
  clickable,
  disabled,
  isSelected,
  onClick,
  children,
}: {
  clickable?: boolean
  disabled: boolean
  isSelected: boolean
  onClick: () => void
  children: ReactNode
}) => (
  <div
    className={cn(
      'border rounded-md',
      disabled && 'opacity-50 cursor-not-allowed',
      clickable && 'cursor-pointer hover:border-primary',
      isSelected && 'border-primary',
      'p-4 flex justify-between items-center'
    )}
    onClick={onClick}
  >
    {children}
  </div>
)

const Emph = ({ children }: { children: ReactNode }) => <Text className='italic'>{children}</Text>

const DisabledOverlay = ({
  message,
  children,
  disable,
}: {
  message: string
  children: React.ReactElement
  disable: boolean
}) => {
  if (!disable) {
    return children
  }
  return (
    <Tooltip>
      <TooltipTrigger>
        <div className='relative'>
          {children}
          <div className='absolute inset-0 bg-white/70 z-10 pointer-events-none' />
        </div>
      </TooltipTrigger>
      <TooltipContent className='flex items-center px-2 py-1'>
        <Text variant='body' className='text-sm'>
          {message}
        </Text>
      </TooltipContent>
    </Tooltip>
  )
}

const InfoIcon = ({ tooltip }: { tooltip?: string }) => {
  return (
    <Tooltip>
      <TooltipTrigger>
        <Icon name='help-circle' size={16} />
      </TooltipTrigger>
      <TooltipContent className='flex items-center px-2 py-1'>
        <Text variant='body' className='text-sm'>
          {tooltip}
        </Text>
      </TooltipContent>
    </Tooltip>
  )
}

const CommitmentVolumeSelector = ({
  products,
  usageType,
  title,
  tooltipContent,
  hasSubscription,
  currentCommitment,
}: {
  products: Product[]
  usageType: string
  title: string
  tooltipContent: string
  hasSubscription: boolean
  currentCommitment: Product | undefined
}) => {
  const sortedProducts = products.sort((a, b) => {
    const amtA = getPaidTierUnitAmount(a)!
    const amtB = getPaidTierUnitAmount(b)!
    return amtB - amtA
  })
  return (
    <SelectedProductsContext.Consumer>
      {({
        allProducts,
        selectedPlan,
        selectedFileCommitment,
        setSelectedFileCommitment,
        selectedPDVCommitment,
        setSelectedPDVCommitment,
      }) => {
        const isProPlan = productIdIsPro(selectedPlan, allProducts)
        return (
          <div>
            <div className='flex items-center justify-between'>
              <SectionSubHeader>{title}</SectionSubHeader>
              <Tooltip>
                <TooltipTrigger>
                  <Icon name='help-circle' className='text-primary' size={16} />
                </TooltipTrigger>
                <TooltipContent className='flex items-center px-2 py-1 max-w-[300px]'>
                  <Text variant='body' className='text-sm'>
                    {tooltipContent}
                  </Text>
                </TooltipContent>
              </Tooltip>
            </div>

            <div className='flex flex-col mt-2 gap-2'>
              {sortedProducts.map((product) => {
                const { metadata, id } = product

                const disabled = !prepayProductIsAvailable(product, hasSubscription, isProPlan)

                const clickable = prepayProductIsAvailableSelfServe(product, hasSubscription, isProPlan)
                const priceDisplay = displayPriceFormatted(getPaidTierUnitAmount(product), 2)
                const unitLabel = metadata?.unitLabel
                const canSelfServe = metadata?.canSelfServe === 'true'
                const commitment = metadata?.commitment
                const current = currentCommitment?.id === id
                const isSelected = !disabled && (selectedFileCommitment === id || selectedPDVCommitment === id)
                return (
                  <CommitmentItem
                    key={id}
                    clickable={clickable}
                    disabled={disabled}
                    isSelected={isSelected}
                    onClick={() => {
                      if (!clickable) {
                        return
                      }
                      if (usageType === 'file') {
                        setSelectedFileCommitment(id)
                      } else if (usageType === 'PDV') {
                        setSelectedPDVCommitment(id)
                      }
                    }}
                  >
                    <div className='flex items-center gap-3'>
                      {canSelfServe && (
                        <div className='rounded-full h-5 w-5 border border-gray-300 flex items-center justify-center'>
                          {isSelected && <div className='rounded-full h-3 w-3 bg-primary'></div>}
                        </div>
                      )}
                      <Text className={cn(isSelected && 'font-medium')}>{commitment}</Text>
                      {current && <CurrentBadge />}
                    </div>
                    <Text className='font-medium'>
                      {canSelfServe ? (
                        <>
                          {priceDisplay}/{unitLabel}
                        </>
                      ) : (
                        <a
                          target='_blank'
                          href='https://flatfile.com/contact-us'
                          rel='noreferrer'
                          className='text-primary hover:underline'
                        >
                          Contact us
                        </a>
                      )}
                    </Text>
                  </CommitmentItem>
                )
              })}
            </div>
          </div>
        )
      }}
    </SelectedProductsContext.Consumer>
  )
}

const PlanIntervalToggleWrapper = ({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: (e: SyntheticEvent) => void
}) => (
  <div className={cn('flex border border-gray-200 rounded-md overflow-hidden divide-x mt-4')} onClick={onClick}>
    {children}
  </div>
)

const PlanIntervalToggleOption = ({ children, active }: { children: ReactNode; active?: boolean }) => (
  <Text
    className={cn(
      'py-1 px-3 text-center flex-1',
      active ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50',
      'capitalize'
    )}
  >
    {children}
  </Text>
)

const PlanIntervalToggle = ({
  plans,
  toggledPlan,
  onClick,
}: {
  plans: IPlan[]
  toggledPlan: string
  onClick: (e: SyntheticEvent) => void
}) => {
  return (
    <PlanIntervalToggleWrapper onClick={onClick}>
      {plans.map((plan) => (
        <PlanIntervalToggleOption active={plan.product.id === toggledPlan} key={plan.product.id}>
          {getIntervalAdverb(plan?.product?.metadata?.interval)}
        </PlanIntervalToggleOption>
      ))}
    </PlanIntervalToggleWrapper>
  )
}

const PlanCardWrapper = ({
  active,
  children,
  onClick,
}: {
  active?: boolean
  children: ReactNode
  onClick: () => void
}) => (
  <div
    className={cn('border rounded-lg shadow-sm transition-all', active && 'border-primary ring-1 ring-primary', 'p-6')}
    onClick={onClick}
  >
    {children}
  </div>
)

const PlanCard = ({
  plans,
  active,
  current,
  currentPlanId,
}: {
  plans: IPlan[]
  active?: boolean
  current?: boolean
  currentPlanId: string | undefined
}) => {
  const startIdx = plans.length > 1 ? 1 : 0
  const [toggledPlan, setToggledPlan] = React.useState(plans[startIdx]?.product.id)

  const plan = findPlanById(plans, toggledPlan)
  const price = getPlanPrice(plan)
  const priceDisplay = displayPriceFormatted(price, 0)
  const planName = planNameDisplay(plan)

  const isCurrentBadgeDisabled = !!currentPlanId && plan?.product.id !== currentPlanId

  // If there is a current active plan, make sure it is toggled on by default
  React.useEffect(() => {
    let toggledPlanIdx
    if (plans.length > 1) {
      const currentPlanIdx = plans.findIndex((p) => p?.product?.id === currentPlanId)
      if (currentPlanIdx !== -1) {
        toggledPlanIdx = currentPlanIdx
      } else {
        toggledPlanIdx = 1
      }
    } else {
      toggledPlanIdx = 0
    }
    setToggledPlan(plans[toggledPlanIdx]?.product?.id)
  }, [currentPlanId])

  return (
    <SelectedProductsContext.Consumer>
      {({ setSelectedPlan }) => (
        <PlanCardWrapper active={active} onClick={() => setSelectedPlan(plan?.product?.id)}>
          <PlanTitleWrapper>
            <div className='flex items-center gap-2'>
              <Text className='uppercase text-xs'>{planName}</Text>
              {current && (
                <CurrentBadge disabled={isCurrentBadgeDisabled} disabledTooltipContent={plan?.disabledTooltipContent} />
              )}
            </div>

            <div className='rounded-full h-4 w-4 border border-gray-300 flex items-center justify-center'>
              {active && <div className='rounded-full h-2 w-2 bg-primary'></div>}
            </div>
          </PlanTitleWrapper>

          <Text className={cn('mt-4 text-[40px] leading-[28px]', active && 'text-primary font-semibold')}>
            {priceDisplay}
            <span className='text-base'>/month</span>
          </Text>
          <div className='flex flex-col gap-4 mt-2'>
            {plan?.subhead && <Text className='text-xs'>{plan?.subhead}</Text>}
            {plan?.lead && <Text className='text-xs'>{plan?.lead}</Text>}
          </div>
          {plans.length > 1 && (
            <PlanIntervalToggle
              plans={plans}
              toggledPlan={toggledPlan as string}
              onClick={(event) => {
                event.stopPropagation()
                if (toggledPlan === plans[0]?.product.id) {
                  setToggledPlan(plans[1]?.product.id)
                  setSelectedPlan(plans[1]?.product.id)
                } else {
                  setToggledPlan(plans[0]?.product.id)
                  setSelectedPlan(plans[0]?.product.id)
                }
              }}
            />
          )}
          <div className='flex flex-col gap-2 mt-4'>
            <Text variant='title3' emphasized>
              {plan?.featuresList?.title}
            </Text>
            {plan?.featuresList?.sections?.map((section) => (
              <div key={section.features[0]}>
                {section.title && (
                  <Text variant='headline' emphasized>
                    {section.title}
                  </Text>
                )}
                <ul className='list-disc pl-5'>
                  {section.features?.map((feature) => (
                    <li key={feature}>
                      <Text>{feature}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </PlanCardWrapper>
      )}
    </SelectedProductsContext.Consumer>
  )
}

export {
  BillingContent,
  BillingSection,
  BillingWrapper,
  CommitmentItem,
  CurrentBadge,
  DisabledOverlay,
  Emph,
  InfoIcon,
  PlanTitleWrapper,
  SectionHeader,
  SectionSubHeader,
  PlanCard,
  PlanCardWrapper,
  CommitmentVolumeSelector,
  SelectedProductsContext,
}
